// @ts-strict-ignore
import type {CohortLiteral} from '../models/builder/cohort-literal';
import type {EventLiteral} from '../models/builder/event-literal';
import type {Literal} from '../models/builder/literal';
import type {PeopleLiteral} from '../models/builder/people-literal';
import {
  BooleanOperator,
  DatetimeOperator,
  ListOperator,
  NumberOperator,
  StringOperator,
  Type,
} from '../models/enums/builder-enums';
import {ArithmeticOperator, TypeCastOperator} from '../models/enums/expression-enums';
import {FilterOperator} from '../models/types/builder-state-types';
import {FilterValue, Operator, Value} from '../models/types/expression-types';
import {SerializedBehaviorWindow, SerializedDatetime} from '../models/types/serialized-cohort-types';
import {
  SerializedCohortClause,
  SerializedCohortLiteral,
  SerializedEventFrequencyClause,
  SerializedEventLiteral,
  SerializedLiteral,
} from '../models/types/serialized-staged-cohort-types';
import {formatDate, parseDate} from './index';

export const getLabelParts = (
  label: string,
  regex: RegExp,
): Array<{text: string; literal: boolean; precedingSpace: boolean}> => {
  const operatorParts = label.split(regex).map((part) => part.trim());
  // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
  const matchParts = label.match(regex);
  return operatorParts
    .reduce((parts, operatorPart, idx) => {
      if (idx < matchParts.length) {
        return [...parts, {text: operatorPart, literal: false}, {text: matchParts[idx], literal: true}];
      } else {
        return [...parts, {text: operatorPart, literal: false}];
      }
    }, [])
    .filter((part) => !!part.text)
    .map((part, idx, arr) => {
      const precedingSpace = idx !== 0 && !part.text.includes(`)`) && !arr[idx - 1].text.endsWith(`(`);
      return {...part, precedingSpace};
    });
};

export const isCohortLiteral = (literal: Literal): literal is CohortLiteral => {
  return (literal as CohortLiteral).clauses[0].cohortId !== undefined;
};

export const isEventLiteral = (literal: Literal): literal is EventLiteral => {
  return (literal as EventLiteral).behaviors !== undefined;
};

export const isPeopleLiteral = (literal: Literal): literal is PeopleLiteral => {
  return !isCohortLiteral(literal) && !isEventLiteral(literal);
};

export const isSerializedCohortLiteral = (literal: SerializedLiteral): literal is SerializedCohortLiteral => {
  return (literal.clauses[0] as SerializedCohortClause).cohortId !== undefined;
};

export const isSerializedEventLiteral = (literal: SerializedLiteral): literal is SerializedEventLiteral => {
  return (literal.clauses[0] as SerializedEventFrequencyClause).behavior !== undefined;
};

export const typeFromTypeCastOperator = (typeCast: TypeCastOperator): Type => {
  switch (typeCast) {
    case TypeCastOperator.Boolean:
      return Type.Boolean;
    case TypeCastOperator.String:
      return Type.String;
    case TypeCastOperator.Number:
      return Type.Number;
    case TypeCastOperator.List:
      return Type.List;
    case TypeCastOperator.Datetime:
      return Type.Datetime;
    default:
      throw new Error(`Invalid type cast operator ${typeCast}`);
  }
};

export const isInvalidStagedFilterClause = (
  type: Type,
  filterOperator: FilterOperator,
  value: FilterValue,
  // TODO opslead
): boolean => {
  const isNumber = (v: any) => !Number.isNaN(parseInt(v, 10));
  switch (type) {
    case Type.String:
      switch (filterOperator) {
        case StringOperator.Contains:
        case StringOperator.DoesNotContain:
          return !value || Array.isArray(value);
        case StringOperator.Equals:
        case StringOperator.DoesNotEqual:
          return !Array.isArray(value) || !value.length;
        default:
          break;
      }
      break;
    case Type.Datetime: {
      let numDays;
      switch (filterOperator) {
        case DatetimeOperator.On:
        case DatetimeOperator.After:
          return !parseDate(value as string);
        case DatetimeOperator.Between:
          return !Array.isArray(value) || !value.every((v) => !!parseDate(v as string));
        case DatetimeOperator.RelativeBeforeInPast:
        case DatetimeOperator.Relative:
          numDays = !value || Array.isArray(value) ? null : (value as SerializedDatetime).window.value;
          return !isNumber(numDays) || numDays < 1;
        case DatetimeOperator.RelativeFuture:
          numDays = !value || Array.isArray(value) ? null : (value as SerializedDatetime).window.value;
          return !isNumber(numDays) || numDays > -1;
        default:
          break;
      }
      break;
    }
    case Type.Number:
      if (filterOperator === NumberOperator.Between) {
        return !Array.isArray(value) || !value.every(isNumber);
      }
      return !isNumber(value);

    case Type.List:
      return !value;
    default:
      break;
  }

  return false;
};

const formatDateString = (dateString: string): string => formatDate(parseDate(dateString), {iso: true});
export const valueToSerializedDatetime = (value: FilterValue, operator: DatetimeOperator): SerializedDatetime => {
  switch (operator) {
    case DatetimeOperator.RelativeBeforeInPast:
      return {to_date: (value as SerializedDatetime).window};
    case DatetimeOperator.Relative:
    case DatetimeOperator.RelativeFuture:
      return value as SerializedDatetime;
    case DatetimeOperator.Between: {
      const [fromDate, toDate] = (value as string[]).map((date) => formatDateString(date));
      return {from_date: fromDate, to_date: toDate};
    }
    case DatetimeOperator.After:
      return {from_date: formatDateString(value as string)} as SerializedDatetime;
    case DatetimeOperator.On: {
      const dateString = formatDateString(value as string);
      return {from_date: dateString, to_date: dateString};
    }
    default:
      throw new Error(`unexpected datetime operator`);
  }
};

// TODO opslead
export const filterOperatorFromOperator = (operator: Operator, type: Type, value: Value): FilterOperator => {
  switch (operator) {
    case ArithmeticOperator.Datetime: {
      const datetimeValue = value as SerializedDatetime;
      if (datetimeValue.window && datetimeValue.window.value >= 0) {
        return DatetimeOperator.Relative;
      } else if (datetimeValue.window && datetimeValue.window.value < 0) {
        return DatetimeOperator.RelativeFuture;
      } else if (datetimeValue.from_date === datetimeValue.to_date) {
        return DatetimeOperator.On;
      } else if (datetimeValue.from_date && datetimeValue.to_date) {
        return DatetimeOperator.Between;
      } else if (datetimeValue.from_date) {
        return DatetimeOperator.After;
      } else if (datetimeValue.to_date && (datetimeValue.to_date as SerializedBehaviorWindow).value) {
        return DatetimeOperator.RelativeBeforeInPast;
      } else {
        throw new Error(`unexpected datetime value`);
      }
    }
    case ArithmeticOperator.Equals:
      switch (type) {
        case Type.String:
          return StringOperator.Equals;
        case Type.Boolean:
          return value ? BooleanOperator.True : BooleanOperator.False;
        case Type.Number:
          return NumberOperator.EqualTo;
        default:
          return StringOperator.Equals;
      }
    case ArithmeticOperator.NotEquals:
      switch (type) {
        case Type.String:
          return StringOperator.DoesNotEqual;
        case Type.Number:
          return NumberOperator.NotEqualTo;
        default:
          return StringOperator.DoesNotEqual;
      }
    case ArithmeticOperator.GreaterThan:
      return NumberOperator.GreaterThan;
    case ArithmeticOperator.LessThan:
      return NumberOperator.LessThan;
    case ArithmeticOperator.LessThanEquals:
      return NumberOperator.AtMost;
    case ArithmeticOperator.GreaterThanEquals:
      return NumberOperator.AtLeast;
    case ArithmeticOperator.In:
      return type === Type.List ? ListOperator.Contains : StringOperator.Contains;
    case ArithmeticOperator.NotIn:
      return type === Type.List ? ListOperator.DoesNotContain : StringOperator.DoesNotContain;
    case ArithmeticOperator.Defined:
      return StringOperator.Set;
    case ArithmeticOperator.NotDefined:
      return StringOperator.NotSet;
    default:
      throw new Error(`Unsupported operator ${operator}`);
  }
};
